/**
 * Theme tokens for the Прогноз/MMS module — РЕДИЗАЙН.
 *
 * Тепер значення беруться з глобальних дизайн-токенів (src/design/tokens.css)
 * через getComputedStyle, тож модуль автоматично відповідає прийнятій концепції
 * (скляні поверхні, Urbanist, догма кольорів, світло/темна) і змінюється разом
 * із темою. Інтерфейс лишився тим самим, щоб не чіпати всі компоненти —
 * змінилося лише ДЖЕРЕЛО кольорів.
 *
 * Серії графіків (лінії/стовпці) лишаються тема-незалежними константами в самих
 * компонентах — там застосовано догму (генерація→зелений, увага→бурштин,
 * нейтраль→сірий, приглушений брендовий для планових ліній, червоний лише для
 * реальної помилки).
 */
import { useTheme } from '@/hooks/useTheme';

export interface FcmmsTokens {
  isDark: boolean;
  pageBg: string;
  cardBg: string;
  cardBorder: string;
  innerBg: string;
  panelBg: string;
  textMain: string;
  textMuted: string;
  textDim: string;
  sep: string;
  rowHi: string;
  rowHover: string;
  brand: string;
  tooltipBg: string;
  tooltipBorder: string;
  inputScheme: 'dark' | 'light';
  periodTileBg: string;
  valueCellBg: string;
  tileHeaderText: string;
  tileValueText: string;
  tileValueNull: string;
  tileUnitText: string;
  tableHeadBg: string;
  tableHeadText: string;
  tableCellText: string;
  chartCursor: string;
  refLine: string;
  ok: string;
  ok2: string;
  warn: string;
  series2: string;
  series3: string;
  bannerOkText: string;
  bannerWarnText: string;
  bannerErrText: string;
  dtMainText: string;
  dtSecText: string;
  dtMissingText: string;
  dtRowAlt: string;
  cloudLowText: string;
  cloudMidText: string;
  cloudHighText: string;
  deltaZeroText: string;
  modalBg: string;
  modalLabelText: string;
}

export function useFcmmsTheme(): FcmmsTokens {
  const { isDark } = useTheme();
  const css = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  const v = (name: string, fb: string) => (css?.getPropertyValue(name).trim() || fb);

  const bg = v('--bg', isDark ? '#28323C' : '#F2F2F2');
  const surface = v('--surface-solid', isDark ? '#2F3A45' : '#FFFFFF');
  const surface2 = v('--surface-2', isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.86)');
  const line = v('--line', isDark ? 'rgba(255,255,255,0.08)' : '#E4E5E7');
  const lineStrong = v('--line-strong', isDark ? 'rgba(255,255,255,0.14)' : '#C9CCCE');
  const text = v('--text', isDark ? '#F2F2F2' : '#28323C');
  const muted = v('--text-muted', isDark ? '#C9CCCE' : '#5E656D');
  const dim = v('--text-dim', isDark ? '#7E868E' : '#9AA0A6');
  const ok = v('--ok', isDark ? '#4CAF6A' : '#2E7D46');
  const ok2 = v('--ok-2', isDark ? '#40C094' : '#1F8A6B');
  const warn = v('--warn', isDark ? '#E0A24A' : '#C77A1B');
  const err = v('--err', isDark ? '#E5484D' : '#CC181B');
  const series2 = v('--series-2', isDark ? '#C9CCCE' : '#5E656D');
  const series3 = v('--series-3', isDark ? '#C56467' : '#A6575B');
  const accent = v('--accent', '#D23538');

  return {
    isDark,
    pageBg:          bg,
    cardBg:          surface,
    cardBorder:      line,
    innerBg:         surface2,
    panelBg:         surface2,
    textMain:        text,
    textMuted:       muted,
    textDim:         dim,
    sep:             lineStrong,
    rowHi:           surface2,
    rowHover:        surface2,
    brand:           accent,
    tooltipBg:       surface,
    tooltipBorder:   line,
    inputScheme:     isDark ? 'dark' : 'light',
    periodTileBg:    surface2,
    valueCellBg:     surface2,
    tileHeaderText:  text,
    tileValueText:   text,
    tileValueNull:   dim,
    tileUnitText:    muted,
    tableHeadBg:     surface2,
    tableHeadText:   muted,
    tableCellText:   text,
    chartCursor:     line,
    refLine:         lineStrong,
    ok,
    ok2,
    warn,
    series2,
    series3,
    bannerOkText:    ok,
    bannerWarnText:  warn,
    bannerErrText:   err,
    dtMainText:      text,
    dtSecText:       muted,
    dtMissingText:   dim,
    dtRowAlt:        surface2,
    cloudLowText:    warn,
    cloudMidText:    series3,
    cloudHighText:   muted,
    deltaZeroText:   text,
    modalBg:         surface,
    modalLabelText:  muted,
  };
}
